import React, { useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { InputField } from "./input-field";
import IconDown from "@/assets/icons/icon-down.svg";

interface SelectFieldProps {
  /** The list of predefined options */
  options: readonly string[];
  /** Currently selected value */
  value?: string;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Callback when a value is selected */
  onChange?: (value: string) => void;
  /** Width class for the trigger and popover (default: "w-[292px]") */
  width?: string;
  /** Whether to show the "기타(직접입력)" custom input row */
  allowCustom?: boolean;
  /** Max length for the custom input */
  customMaxLength?: number;
}

export const SelectFieldCountry: React.FC<SelectFieldProps> = ({
  options,
  value,
  placeholder = "소재국을 선택해주세요",
  onChange,
  width = "w-full lg:w-[292px]",
  allowCustom = false,
  customMaxLength = 50,
}) => {
  const [open, setOpen] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (opt: string) => {
    setIsCustom(false);
    onChange?.(opt);
    setOpen(false);
  };

  const handleCustomClick = () => {
    if (!isCustom) {
      setIsCustom(true);
      setCustomInput("");
      onChange?.("");
      setTimeout(() => customInputRef.current?.focus(), 50);
    }
  };

  const commitCustomValue = (val: string) => {
    const trimmed = val.trim();
    if (trimmed) {
      onChange?.(trimmed);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={`flex h-12 ${width} items-center justify-between rounded-[4px] bg-white border px-4 text-base leading-6 tracking-[-0.01em] text-black font-bold focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 ${open ? "border-black" : "border-[#00000027]"}`}
        >
          <span
            className={`overflow-hidden text-ellipsis whitespace-nowrap ${value ? "" : "text-[#B0B0B0] font-normal"}`}
          >
            {value ? (options.includes(value as string) ? value : `기타 (${value})`) : placeholder}
          </span>
          <img
            src={IconDown}
            alt=""
            className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-1 overflow-hidden"
        align="start"
        sideOffset={4}
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        {options.map((opt) => (
          <div
            key={opt}
            className={`relative outline-none flex w-full cursor-pointer select-none items-center rounded-sm p-2 text-sm font-bold hover:bg-[#EBEBEB] ${value === opt ? "bg-[#EBEBEB]" : ""}`}
            onClick={() => handleSelect(opt)}
          >
            {opt}
          </div>
        ))}

        {allowCustom && (
          <div className="relative flex w-full items-center gap-2 rounded-sm py-1.5 px-3 overflow-hidden">
            <span
              className="text-sm font-bold whitespace-nowrap cursor-pointer"
              onClick={handleCustomClick}
            >
              기타(직접입력)
            </span>
            {isCustom && (
              <InputField
                ref={customInputRef}
                placeholder="국가명을 입력..."
                maxLength={customMaxLength}
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onBlur={() => commitCustomValue(customInput)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const val = customInput.trim();
                    if (val) {
                      onChange?.(val);
                      setOpen(false);
                    }
                  }
                }}
              />
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
